import { apiClient } from './client';

// ============================================
// Types
// ============================================

/**
 * Lifecycle of a stream entry. Job-style events (export, import) are born
 * `pending` at request time, and the SAME entry flips in place to its outcome
 * when the async job finishes. Instantaneous events are born `success`.
 */
export type StreamStatus = 'pending' | 'success' | 'failure';

export interface StreamEntry {
  id: number;
  eventType: string;
  /** Lifecycle state — drives in-progress vs. ready/failed rendering. */
  status: StreamStatus;
  /** Stable job identity (e.g. "export:123"); null for instantaneous events. */
  subject: string | null;
  message: string;
  context: Record<string, unknown> | null;
  viewed: boolean;
  created: string;
  label: string;
  icon: string;
  // 'download' — actionUrl is an API endpoint; fetch it for a signed URL.
  // 'detail'   — expand the entry context inline.
  // 'navigate' — actionUrl is a relative app path; client-side route to it.
  action: 'download' | 'detail' | 'navigate' | null;
  actionUrl: string | null;
  /**
   * Scope the entry was recorded in (`StreamEntryResponse.organization_id` /
   * `network_id`). Optional: fixtures and older payloads may omit them.
   */
  organizationId?: number | null;
  networkId?: number | null;
}

export interface StreamListResponse {
  entries: StreamEntry[];
  totalCount: number;
  unreadCount: number;
}

// ============================================
// API Functions
// ============================================

export async function fetchStream(limit = 50, offset = 0): Promise<StreamListResponse> {
  const response = await apiClient.get<StreamListResponse>('/api/stream/', {
    params: { limit, offset },
  });
  return response.data;
}

export async function fetchUnreadCount(): Promise<number> {
  const response = await apiClient.get<{ count: number }>('/api/stream/unread-count/');
  return response.data.count;
}

/**
 * Mutation key of the mark-read save. The ctx-param gate's write-safety hold
 * ignores it: a notification's View must navigate without waiting on it
 * (Q-506 card 1571).
 */
export const STREAM_MARK_READ_MUTATION_KEY = ['stream', 'mark-read'] as const;

export async function markRead(entryIds?: number[]): Promise<number> {
  const response = await apiClient.post<{ updated: number }>('/api/stream/mark-read/', {
    entryIds: entryIds ?? null,
  });
  return response.data.updated;
}

export async function fetchDownloadUrl(apiEndpoint: string): Promise<string> {
  // Audit low: apiEndpoint is server-provided today, but if it were ever an
  // absolute URL, axios would ignore baseURL and send the Authorization
  // header off-origin. Constrain it to our API namespace.
  if (!apiEndpoint.startsWith('/api/')) {
    throw new Error(`fetchDownloadUrl: refusing non-API action URL "${apiEndpoint}"`);
  }
  const response = await apiClient.get<{ downloadUrl: string }>(apiEndpoint);
  return response.data.downloadUrl;
}
